import { createDisplayNumberFormat } from '../utils/displayNumberFormat';
import {HomeFitnessRamp} from './HomeFitnessRamp';
import { CHART_SERIES } from './charts/palette';
import { NmTabNav } from './NmTabNav';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReadinessHistory, TrendDailyEntry } from '../services/api';

type MetricKey = 'readiness' | 'tss' | 'distance' | 'time';

const TABS: { key: MetricKey; labelKey: string; fallback: string }[] = [
  { key: 'readiness', labelKey: 'home.trend.readiness', fallback: 'Readiness' },
  { key: 'tss', labelKey: 'home.trend.tss', fallback: 'TSS' },
  { key: 'distance', labelKey: 'home.trend.distance', fallback: 'Distance' },
  { key: 'time', labelKey: 'home.trend.time', fallback: 'Time' },
];

const DAY_NAMES_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FA = ['ی', 'د', 'س', 'چ', 'پ', 'ج', 'ش'];

interface BarItem {
  label: string;
  value: number;
  displayValue: string;
  barValue: string;
  color: string;
  color2: string;
}

function TrendBarChart({ data, height = 120 }: { data: BarItem[]; height?: number }) {
  const values = data.map((d) => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const valueRange = maxVal - minVal;
  const labelRow = 18;
  const plotHeight = Math.max(48, height - labelRow - 36);

  return (
    <div
      className="ltr-island"
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'flex-end',
        height,
        padding: '8px 4px',
        boxSizing: 'border-box',
        flex: 1,
      }}
    >
      {data.map((item, i) => {
        // A local-range scale makes small day-to-day changes legible while keeping
        // positive columns legible; zero values stay on the baseline.
        const normalized = valueRange > 0 ? (item.value - minVal) / valueRange : 0.6;
        const barHeight = item.value > 0 ? Math.round(plotHeight * (0.38 + normalized * 0.62)) : 0;
        return (
          <div
            key={i}
            className="home-trend-bar"
            tabIndex={0}
            aria-label={`${item.label}: ${item.displayValue}`}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 6,
              minWidth: 0,
              height: plotHeight + labelRow + 20,
            }}
          >
            <span className="home-trend-bar__value" style={{ visibility: item.value > 0 ? 'visible' : 'hidden' }}>{item.barValue}</span>
            <div className="home-trend-bar__plot" style={{ height: plotHeight }}>
              <span className="home-trend-bar__tooltip" style={{ bottom: barHeight + 7 }}>
                {item.displayValue}
              </span>
              <div
                className="home-trend-bar__column"
                style={{
                  width: '100%',
                  maxWidth: 28,
                  borderRadius: 4,
                  height: barHeight,
                  padding: item.value > 0 ? undefined : 0,
                  background: item.color,
                  transition: 'height 0.4s ease',
                  flexShrink: 0,
                }}
              >
              </div>
            </div>
            <span className="home-trend-bar__day-label"
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: 'var(--fg-4)',
                fontFamily: 'var(--font-mono)',
                lineHeight: 1,
                textAlign: 'center',
                width: '100%',
              }}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface HomeTrendCardProps {
  history?: ReadinessHistory[];
  daily?: TrendDailyEntry[];
  /** Taller chart area for desktop readiness widgets. */
  chartHeight?: number;
  athleteId?: number | null;
  refreshKey?: unknown;
}

function metricValue(entry: TrendDailyEntry, tab: MetricKey): number {
  const s = entry.summary;
  if (tab === 'time') return s.total_time.actual;
  if (tab === 'distance') return s.total_distance.actual;
  return s.total_tss.actual;
}

/** 7-day trend — readiness score + daily time / distance / TSS from home API. */
export function HomeTrendCard({ history = [], daily = [], chartHeight = 120, athleteId, refreshKey }: HomeTrendCardProps) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<MetricKey>('readiness');

  const dayLabels = i18n.language === 'fa' || i18n.language === 'ar' ? DAY_NAMES_FA : DAY_NAMES_EN;

  const formatNumber = (value: number, maximumFractionDigits = 0) => createDisplayNumberFormat(i18n.language, {
    maximumFractionDigits,
  }).format(value);

  const formatBarNumber = (value: number, maximumFractionDigits = 0) => createDisplayNumberFormat(i18n.resolvedLanguage ?? i18n.language, {
    maximumFractionDigits,
  }).format(value);

  const formatBarValue = (value: number, metric: MetricKey): string => {
    if (metric === 'tss') return formatBarNumber(value, value < 10 ? 1 : 0);
    if (metric === 'distance') return formatBarNumber(value / 1000);
    if (value >= 3600) return `${formatBarNumber(value / 3600, 1)}h`;
    return `${formatBarNumber(value / 60)}m`;
  };

  const formatMetricValue = (value: number, metric: MetricKey): string => {
    if (metric === 'tss') return `${formatNumber(value)} TSS`;
    if (metric === 'distance') return `${formatNumber(value / 1000)} ${t('home.trend.units.km')}`;
    if (value >= 3600) return `${formatNumber(value / 3600, 1)} ${t('home.trend.units.hour')}`;
    return `${formatNumber(value / 60)} ${t('home.trend.units.minute')}`;
  };

  const barData = useMemo((): BarItem[] => {
    if (activeTab === 'readiness') return [];

    if (!daily.length) return [];

    return daily.slice(-7).map((entry) => {
      const date = new Date(entry.date);
      const value = metricValue(entry, activeTab);
      return {
        label: dayLabels[date.getDay()] ?? '',
        value,
        displayValue: formatMetricValue(value, activeTab),
        barValue: formatBarValue(value, activeTab),
        color: CHART_SERIES.secondary,
        color2: CHART_SERIES.secondary,
      };
    });
  }, [history, daily, activeTab, dayLabels, i18n.language, t]);

  const hasAnyData = !!athleteId || history.length > 0 || daily.length > 0;
  if (!hasAnyData) return null;

  return (
    <div className="home-trend-card" style={styles.card}>
      <NmTabNav
        className="home-metric-tabs"
        items={TABS.map((tab) => ({ value: tab.key, label: t(tab.labelKey, tab.fallback) }))}
        value={activeTab}
        onChange={setActiveTab}
        ariaLabel={t('home.trend.title', 'Readiness Trend')}
      />

      <div className="home-trend-card__plot" style={{ ...styles.chartWell, minHeight: chartHeight, flex: chartHeight > 120 ? 1 : undefined }}>
        {activeTab === 'readiness' ? <HomeFitnessRamp athleteId={athleteId} refreshKey={refreshKey} /> : barData.length > 0 && barData.some((b) => b.value > 0) ? (
          <TrendBarChart data={barData} height={chartHeight} />
        ) : (
          <div style={{ ...styles.noData, height: chartHeight }}>{t('home.trend.noData', 'No data available')}</div>
        )}
      </div>


    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    borderRadius: 'var(--r-4)',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised)',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  cardTitle: {
    fontSize: 'var(--fs-body)',
    fontWeight: 700,
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
    textAlign: 'start',
  },
  cardSub: {
    fontSize: 'var(--fs-caption)',
    color: 'var(--fg-4)',
    letterSpacing: '0.04em',
    textAlign: 'start',
    marginInlineStart: 'auto',
  },
  chartWell: {
    borderRadius: 'var(--r-3)',
    background: 'var(--surface-sunken)',
    boxShadow: 'var(--nm-inset-sm)',
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column',
  },
  noData: {
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'var(--fs-small)',
    color: 'var(--fg-4)',
  },
  legend: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 14,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  legendDot: {
    width: 10,
    height: 4,
    borderRadius: 2,
    display: 'inline-block',
  },
  legendLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--fg-4)',
  },
};
